import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { DocumentSchemasService } from '../../../api/services/document-schemas.service';
import type { DocumentSchemaResponseModel } from '../../../api/models/document-schema-response-model';

export const STEUREN_DOCUMENT_SCHEMA_PRODUCT_CODES = {
  Salarystatement: 'Salarystatement',
  UnemploymentInsuranceStatement: 'UnemploymentInsuranceStatement',
  SocialInsuranceStatement: 'SocialInsuranceStatement',
  Bankstatement: 'Bankstatement',
  OtherAssetsStatement: 'OtherAssetsStatement',
  PropertyStatement: 'PropertyStatement',
  HealthInsuranceStatement: 'HealthInsuranceStatement',
  HealthInsuranceOtherStatement: 'HealthInsuranceOtherStatement',
  '3astatement': '3astatement',
  OtherDocuments: 'OtherDocuments',
  Taxstatement: 'Taxstatement',
} as const;

/**
 * Hardcoded `typeCode` (Salesforce DeveloperName) values sent on
 * POST /api/Documents/upload. Keyed by upload trigger / UI context, not by
 * the local product-code enum — multiple product codes can share a typeCode,
 * and Säule 3b has no matching product code today.
 */
export const STEUERN_DOCUMENT_TYPE_CODES = {
  Lohnausweis: 'Steuer_Lohnausweis',
  BankStatement: 'Steuer_BankStatement',
  '3aStatement': 'Steuer_3aStatement',
  '3bStatement': 'Steuer_3bStatement',
  HealthInsurance: 'Steuer_HealthInsurance',
  Andere: 'Steuer_Andere',
} as const;

/**
 * Hardcoded `documentTypeId` values (document types from
 * GET /api/Documents/types) sent on POST /api/Documents/upload. Keyed by
 * upload trigger / UI context like `STEUERN_DOCUMENT_TYPE_CODES` — several
 * tax-flow uploads share the generic "Steuererklärung - Andere" type.
 */
export const STEUERN_DOCUMENT_TYPE_IDS = {
  /** Backend document type "Steuererklärung Vorjahr". */
  SteuererklaerungVorjahr: '505e45c8-6109-4fb8-b0ac-8203c9fc991f',
  /** Backend document type "Steuererklärung - Andere". */
  SteuererklaerungAndere: '65f0a990-7199-5a04-b473-3499536065b6',
  /** Backend document type "Lohnausweis". */
  Lohnausweis: '61dc1cf4-8935-46f5-8723-7f93399dbdae',
  /** Backend document type "Bank-Steuerbescheinigung". */
  BankSteuerbescheinigung: '0ba8fe09-1ef4-477c-9484-425fa31344e2',
} as const;

export const STEUREN_DOCUMENT_SCHEMA_IDS: Record<string, string> = {
  [STEUREN_DOCUMENT_SCHEMA_PRODUCT_CODES.Bankstatement]: '5fc7e3e0-1a8b-4f69-82c5-9c5c8dac247a',
  [STEUREN_DOCUMENT_SCHEMA_PRODUCT_CODES.HealthInsuranceOtherStatement]: 'bf42835e-dd67-4c46-9f92-84a074f1850f',
  [STEUREN_DOCUMENT_SCHEMA_PRODUCT_CODES.OtherDocuments]: '8d84b46a-5eed-4084-b567-90aead3b8a3e',
  [STEUREN_DOCUMENT_SCHEMA_PRODUCT_CODES.OtherAssetsStatement]: '87264212-c9b4-4333-a0f3-a8a41b396e3d',
  [STEUREN_DOCUMENT_SCHEMA_PRODUCT_CODES.PropertyStatement]: 'a99c35a3-2591-4aad-9631-4d4fabd7a1de',
  [STEUREN_DOCUMENT_SCHEMA_PRODUCT_CODES.Salarystatement]: '4c3c41ca-0bfc-47a3-a05a-8a492a50bfd0',
  [STEUREN_DOCUMENT_SCHEMA_PRODUCT_CODES.Taxstatement]: '2c2623c3-54bf-46ff-aa42-6b3f80d9f6b1',
  [STEUREN_DOCUMENT_SCHEMA_PRODUCT_CODES.HealthInsuranceStatement]: '0d9e0f21-7efc-4c0c-a9a3-ddef18676c02',
  [STEUREN_DOCUMENT_SCHEMA_PRODUCT_CODES.UnemploymentInsuranceStatement]: '099aecf9-6790-43c2-9831-3e1a89db574d',
  [STEUREN_DOCUMENT_SCHEMA_PRODUCT_CODES['3astatement']]: 'fe7a8d0a-2c99-44ee-88f3-fdff017a64a1',
  [STEUREN_DOCUMENT_SCHEMA_PRODUCT_CODES.SocialInsuranceStatement]: '91b55402-0b6f-41c1-a1ee-3bec9df6d627',
};

type SchemaWithProductCode = DocumentSchemaResponseModel & { productCode?: string | null };

@Injectable({ providedIn: 'root' })
export class SteuernDocumentSchemaService {
  private schemas$: Observable<DocumentSchemaResponseModel[]> | null = null;

  constructor(private readonly documentSchemasService: DocumentSchemasService) {}

  getSchemas(): Observable<DocumentSchemaResponseModel[]> {
    if (!this.schemas$) {
      this.schemas$ = this.documentSchemasService
        .apiDocumentSchemasContractsSchemasGet$Json()
        .pipe(shareReplay(1));
    }
    return this.schemas$;
  }

  getSchemaIdByProductCode(productCodeSuffix: string): Observable<string> {
    const fixedId = STEUREN_DOCUMENT_SCHEMA_IDS[productCodeSuffix];
    if (fixedId) {
      return of(fixedId);
    }
    return this.getSchemas().pipe(
      map((schemas) => {
        const list = schemas ?? [];
        const withCode = list as SchemaWithProductCode[];
        const byProductCode = withCode.find(
          (s) =>
            s.productCode != null &&
            (s.productCode === productCodeSuffix ||
              s.productCode.endsWith('#' + productCodeSuffix) ||
              s.productCode.endsWith(productCodeSuffix))
        );
        if (byProductCode?.id) return byProductCode.id;
        const byName = list.find((s) =>
          (s.name ?? '').toLowerCase().includes(productCodeSuffix.toLowerCase())
        );
        return byName?.id ?? list[0]?.id ?? '';
      })
    );
  }
}
